import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, CheckCircle, Mail, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';

const emailSchema = z.string().email('Please enter a valid email address');

interface SaveCalculationModalProps {
  isOpen: boolean;
  onClose: () => void;
  calculationData: {
    monthlyPayment?: number;
    homePrice?: number;
    downPayment?: number;
    loanAmount?: number;
    interestRate?: number;
    closingCosts?: number;
    mortgageData?: any;
  };
  userEmail?: string;
}

export const SaveCalculationModal: React.FC<SaveCalculationModalProps> = ({
  isOpen,
  onClose,
  calculationData,
  userEmail
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [email, setEmail] = useState(userEmail || '');
  const [emailError, setEmailError] = useState('');

  // Update email when userEmail changes
  useEffect(() => {
    if (userEmail) {
      setEmail(userEmail);
    }
  }, [userEmail]);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) {
      setEmailError('');
    }
  };

  const handleSendReport = async () => {
    try {
      const result = emailSchema.safeParse(email);
      if (!result.success) {
        setEmailError(result.error.errors[0]?.message || 'Invalid email');
        return;
      }

      setIsSubmitting(true);

      // Send calculation email to the user
      const { error: emailError } = await supabase.functions.invoke('send-saved-calculation', {
        body: {
          email: email,
          homePrice: calculationData.homePrice,
          monthlyPayment: calculationData.monthlyPayment,
          downPayment: calculationData.downPayment,
          loanAmount: calculationData.loanAmount,
          interestRate: calculationData.interestRate,
          closingCosts: calculationData.closingCosts,
        }
      });

      if (emailError) {
        throw emailError;
      }

      setIsSuccess(true);
      toast({
        title: 'Report Sent!',
        description: 'Check your email for a copy of your calculation.',
      });
    } catch (error) {
      console.error('Error sending calculation:', error);
      toast({
        title: 'Error',
        description: 'Failed to send your calculation. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setEmail(userEmail || '');
    setEmailError('');
    setIsSuccess(false);
    onClose();
  };

  if (isSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={resetAndClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center py-6 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <DialogTitle className="text-xl mb-2">Report Sent!</DialogTitle>
            <DialogDescription className="text-muted-foreground mb-6">
              We've emailed your calculation report to {email}
            </DialogDescription>
            
            <Button onClick={resetAndClose} className="w-full" size="lg">
              Continue Calculating
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Save className="w-5 h-5" />
            Email Your Calculation
          </DialogTitle>
          <DialogDescription>
            Send a detailed report of your mortgage calculation to your email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Calculation Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Home Price</span>
              <span className="font-medium">${calculationData.homePrice?.toLocaleString() || '0'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Down Payment</span>
              <span className="font-medium">${calculationData.downPayment?.toLocaleString() || '0'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Monthly Payment</span>
              <span className="font-semibold text-primary">${calculationData.monthlyPayment?.toLocaleString() || '0'}/mo</span>
            </div>
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              className={emailError ? 'border-destructive' : ''}
            />
            {emailError && <p className="text-xs text-destructive">{emailError}</p>}
          </div>

          {/* Send Button */}
          <Button
            onClick={handleSendReport}
            disabled={isSubmitting || !email}
            className="w-full"
            size="lg"
          >
            {isSubmitting ? (
              <>Sending...</>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Report
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
